/**
 * Maps Azure Document Intelligence `prebuilt-healthInsuranceCard.us` analyze result
 * into Patient Intake form fields.
 *
 * Azure often omits `Member.Name` and puts the primary subscriber under
 * `Dependents[0].Name` (member index 01 on the card).
 *
 * @see https://learn.microsoft.com/azure/ai-services/document-intelligence/prebuilt/health-insurance-card
 */

/** Subset of PatientIntake FormData filled from the card. */
export type IntakeFormPatchFromHealthCard = {
  patientFirstName: string
  patientLastName: string
  patientDOB: string
  patientGender: string
  patientZip: string
  patientPhone: string
  patientEmail: string
  subscriberFirstName: string
  subscriberLastName: string
  subscriberDOB: string
  subscriberID: string
  /** Insurance ID of the selected card person (subscriber or dependent). */
  memberID: string
  relationToSubscriber: string
  subscriberZip: string
  payor: string
  payerCode: string
  groupNumber: string
  policyNumber: string
  /** Plan effective date → Service Date From (step 2). */
  serviceDateFrom: string
}

const EMPTY: IntakeFormPatchFromHealthCard = {
  patientFirstName: '',
  patientLastName: '',
  patientDOB: '',
  patientGender: '',
  patientZip: '',
  patientPhone: '',
  patientEmail: '',
  subscriberFirstName: '',
  subscriberLastName: '',
  subscriberDOB: '',
  subscriberID: '',
  memberID: '',
  relationToSubscriber: 'self',
  subscriberZip: '',
  payor: '',
  payerCode: '',
  groupNumber: '',
  policyNumber: '',
  serviceDateFrom: '',
}

function fieldAsString(field: unknown): string {
  if (field === null || field === undefined) return ''
  if (typeof field === 'string') return field.trim()
  if (typeof field !== 'object') return String(field).trim()
  const o = field as Record<string, unknown>
  if (typeof o.valueString === 'string') return o.valueString.trim()
  if (typeof o.valueDate === 'string') return o.valueDate.trim()
  if (typeof o.valuePhoneNumber === 'string') return o.valuePhoneNumber.trim()
  if (typeof o.valueNumber === 'number' && Number.isFinite(o.valueNumber)) {
    return String(o.valueNumber)
  }
  if (typeof o.content === 'string') return o.content.trim()
  return ''
}

/** Unwraps `valueObject` on a document field (nested fields). */
function fieldAsObject(field: unknown): Record<string, unknown> | null {
  if (!field || typeof field !== 'object') return null
  const o = field as Record<string, unknown>
  const vo = o.valueObject
  if (vo && typeof vo === 'object' && !Array.isArray(vo)) {
    return vo as Record<string, unknown>
  }
  return null
}

function normalizeDateForInput(raw: string): string {
  const t = raw.trim()
  if (!t) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10)
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (m) {
    const mm = m[1].padStart(2, '0')
    const dd = m[2].padStart(2, '0')
    const yy = m[3]
    return `${yy}-${mm}-${dd}`
  }
  return t
}

function splitFullName(name: string): { first: string; last: string } {
  const t = name.trim()
  if (!t) return { first: '', last: '' }
  const parts = t.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return { first: parts[0], last: '' }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

function normalizeGender(g: string): string {
  const u = g.trim().toUpperCase()
  if (u === 'M' || u === 'MALE') return 'male'
  if (u === 'F' || u === 'FEMALE') return 'female'
  const lower = g.trim().toLowerCase()
  if (lower === 'male' || lower === 'female') return lower
  return g.trim()
}

function extractZipFromAddress(address: string): string {
  const m = address.match(/\b(\d{5})(-\d{4})?\b/)
  return m ? `${m[1]}${m[2] ?? ''}` : ''
}

/** `address` fields expose `valueAddress.postalCode` in addition to `content`. */
function postalCodeFromAddressField(addressField: unknown): string {
  if (!addressField || typeof addressField !== 'object') return ''
  const o = addressField as Record<string, unknown>
  const va = o.valueAddress
  if (va && typeof va === 'object') {
    const pc = (va as Record<string, unknown>).postalCode
    if (typeof pc === 'string' && pc.trim()) return pc.trim()
  }
  return extractZipFromAddress(fieldAsString(addressField))
}

/** Names listed under `Dependents` (first entry is often the primary / 01 line). */
function getDependentNames(fields: Record<string, unknown>): string[] {
  return getDependentEntries(fields)
    .map((e) => e.name)
    .filter(Boolean)
}

export type HealthCardPersonOption = {
  /** `member` = subscriber line on card; `dep:0`, `dep:1`, … = Dependents index */
  id: string
  /** Full name for display */
  label: string
}

export type HealthCardPersonIdentity = HealthCardPersonOption & {
  firstName: string
  lastName: string
  dateOfBirth: string
  gender: string
  /** Person-specific insurance ID when the card has one; otherwise the subscriber ID. */
  memberId: string
}

type DependentEntry = { name: string; birthDate: string; gender: string; memberId: string }

function idNumberFromObject(obj: Record<string, unknown> | null | undefined): string {
  if (!obj) return ''
  const idField = obj.IdNumber
  const idObj = fieldAsObject(idField)
  const built = buildMemberId(
    idObj ? fieldAsString(idObj.Prefix) : '',
    idObj ? fieldAsString(idObj.Number) : fieldAsString(idField),
    fieldAsString(obj.IdNumberSuffix)
  )
  return (
    built ||
    fieldAsString(obj.MemberId) ||
    fieldAsString(obj.MemberID) ||
    fieldAsString(obj.Id)
  )
}

function getDependentEntries(
  fields: Record<string, unknown>
): DependentEntry[] {
  const depField = fields.Dependents
  if (!depField || typeof depField !== 'object') return []
  const valueArray = (depField as Record<string, unknown>).valueArray
  if (!Array.isArray(valueArray)) return []
  const out: DependentEntry[] = []
  for (const entry of valueArray) {
    const vo = fieldAsObject(entry)
    if (!vo) continue
    const name = fieldAsString(vo.Name).trim()
    if (!name) continue
    out.push({
      name,
      birthDate: fieldAsString(vo.BirthDate),
      gender: fieldAsString(vo.Gender),
      memberId: idNumberFromObject(vo),
    })
  }
  return out
}

function getHealthCardPersonIdentities(
  fields: Record<string, unknown>,
  memberObj: Record<string, unknown> | null
): HealthCardPersonIdentity[] {
  const subscriberId = subscriberIdFromCard(fields, memberObj)
  const out: HealthCardPersonIdentity[] = []
  const memberName = memberObj ? fieldAsString(memberObj.Name).trim() : ''
  if (memberName && memberObj) {
    const { first, last } = splitFullName(memberName)
    out.push({
      id: 'member',
      label: memberName,
      firstName: first,
      lastName: last,
      dateOfBirth: normalizeDateForInput(fieldAsString(memberObj.BirthDate)),
      gender: normalizeGender(fieldAsString(memberObj.Gender)),
      memberId: idNumberFromObject(memberObj) || subscriberId,
    })
  }
  getDependentEntries(fields).forEach((entry, index) => {
    const { first, last } = splitFullName(entry.name)
    out.push({
      id: `dep:${index}`,
      label: entry.name,
      firstName: first,
      lastName: last,
      dateOfBirth: normalizeDateForInput(entry.birthDate),
      gender: normalizeGender(entry.gender),
      memberId: entry.memberId || subscriberId,
    })
  })
  return out
}

/**
 * People named on the card (Member + each Dependent with a name).
 * The first record is always the subscriber, whether or not the user selects it.
 */
export function listHealthCardPersonIdentities(
  payload: unknown
): HealthCardPersonIdentity[] {
  const fields = getFirstDocumentFields(payload)
  if (!fields) return []
  const memberObj = fieldAsObject(fields.Member)
  return getHealthCardPersonIdentities(fields, memberObj)
}

export function listHealthCardPersons(payload: unknown): HealthCardPersonOption[] {
  return listHealthCardPersonIdentities(payload).map(({ id, label }) => ({
    id,
    label,
  }))
}

function personPatchFromCardPeople(
  people: HealthCardPersonIdentity[],
  personId?: string
): Pick<
  IntakeFormPatchFromHealthCard,
  | 'patientFirstName'
  | 'patientLastName'
  | 'patientDOB'
  | 'patientGender'
  | 'subscriberFirstName'
  | 'subscriberLastName'
  | 'subscriberDOB'
  | 'relationToSubscriber'
  | 'memberID'
> {
  const primary = people[0]
  const selected =
    (personId ? people.find((person) => person.id === personId) : undefined) ?? primary
  const selectedIsPrimary = selected.id === primary.id
  return {
    patientFirstName: selected.firstName,
    patientLastName: selected.lastName,
    patientDOB: selected.dateOfBirth,
    patientGender: selected.gender,
    subscriberFirstName: primary.firstName,
    subscriberLastName: primary.lastName,
    subscriberDOB: primary.dateOfBirth,
    relationToSubscriber: selectedIsPrimary ? 'self' : 'child',
    memberID: selected.memberId || primary.memberId,
  }
}

export function normalizeInsurerKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[®©™]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Match OCR insurer string (e.g. `aetna®`) to a pVerify payer row for dropdown + code. */
export function matchPverifyPayerForInsurer<
  T extends { payerName: string; payerCode: string },
>(insurerRaw: string, rows: T[]): T | undefined {
  const key = normalizeInsurerKey(insurerRaw)
  if (!key) return undefined
  const byExact = rows.find((r) => normalizeInsurerKey(r.payerName) === key)
  if (byExact) return byExact
  return rows.find((r) => {
    const rk = normalizeInsurerKey(r.payerName)
    return rk.includes(key) || key.includes(rk)
  })
}

function getFirstDocumentFields(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null
  const ar = (payload as Record<string, unknown>).analyzeResult
  if (!ar || typeof ar !== 'object') return null
  const docs = (ar as Record<string, unknown>).documents
  if (!Array.isArray(docs) || docs.length === 0) return null
  const d0 = docs[0] as Record<string, unknown>
  const fields = d0.fields
  if (!fields || typeof fields !== 'object') return null
  return fields as Record<string, unknown>
}

function buildMemberId(prefix: string, number: string, suffix: string): string {
  return [prefix, number, suffix]
    .map((s) => String(s ?? '').trim())
    .filter(Boolean)
    .join('')
}

function subscriberIdFromCard(
  fields: Record<string, unknown>,
  memberObj: Record<string, unknown> | null
): string {
  const idNumField = fields.IdNumber
  const idObj = fieldAsObject(idNumField)
  const idPrefix = idObj ? fieldAsString(idObj.Prefix) : ''
  const idNumber = idObj ? fieldAsString(idObj.Number) : fieldAsString(idNumField)
  const idSuffix = memberObj ? fieldAsString(memberObj.IdNumberSuffix) : ''
  return buildMemberId(idPrefix, idNumber, idSuffix) || idNumberFromObject(memberObj)
}

function buildCommonInsuranceFields(
  fields: Record<string, unknown>,
  memberObj: Record<string, unknown> | null
): Pick<
  IntakeFormPatchFromHealthCard,
  | 'patientZip'
  | 'patientPhone'
  | 'patientEmail'
  | 'subscriberID'
  | 'subscriberZip'
  | 'payor'
  | 'payerCode'
  | 'groupNumber'
  | 'policyNumber'
  | 'serviceDateFrom'
> {
  const insurer = fieldAsString(fields.Insurer)
  const idSuffix = memberObj ? fieldAsString(memberObj.IdNumberSuffix) : ''

  const idNumField = fields.IdNumber
  const idObj = fieldAsObject(idNumField)
  const idPrefix = idObj ? fieldAsString(idObj.Prefix) : ''
  const idNumber = idObj ? fieldAsString(idObj.Number) : ''
  const subscriberId = buildMemberId(idPrefix, idNumber, idSuffix)

  const groupNumber = fieldAsString(fields.GroupNumber)

  const planField = fields.Plan
  const planObj = fieldAsObject(planField)
  const planNumber = planObj ? fieldAsString(planObj.Number) : ''

  const rxField = fields.PrescriptionInfo
  const rxObj = fieldAsObject(rxField)
  const rxId = rxObj ? fieldAsString(rxObj.RxId) : ''

  const policyNumber = planNumber || rxId

  const payerField = fields.Payer
  const payerObj = fieldAsObject(payerField)
  let payerPhone = ''
  let payerZip = ''
  if (payerObj) {
    payerPhone = fieldAsString(payerObj.PhoneNumber)
    const addrField = payerObj.Address
    payerZip = postalCodeFromAddressField(addrField)
  }

  const effectiveRaw = fieldAsString(fields.EffectiveDate)
  const serviceDateFrom = normalizeDateForInput(effectiveRaw)

  return {
    patientZip: payerZip,
    patientPhone: payerPhone,
    patientEmail: '',
    subscriberID: subscriberId,
    subscriberZip: payerZip,
    payor: insurer,
    payerCode: '',
    groupNumber,
    policyNumber,
    serviceDateFrom,
  }
}

function personPatchLegacy(
  fields: Record<string, unknown>,
  memberObj: Record<string, unknown> | null
): Pick<
  IntakeFormPatchFromHealthCard,
  | 'patientFirstName'
  | 'patientLastName'
  | 'patientDOB'
  | 'patientGender'
  | 'subscriberFirstName'
  | 'subscriberLastName'
  | 'subscriberDOB'
  | 'relationToSubscriber'
  | 'memberID'
> {
  const memberNameFromMember = memberObj ? fieldAsString(memberObj.Name) : ''
  const dependentNames = getDependentNames(fields)
  const memberNameRaw = memberNameFromMember || dependentNames[0] || ''
  const memberDobRaw = memberObj ? fieldAsString(memberObj.BirthDate) : ''
  const memberGenderRaw = memberObj ? fieldAsString(memberObj.Gender) : ''
  const { first: firstName, last: lastName } = splitFullName(memberNameRaw)
  const dob = normalizeDateForInput(memberDobRaw)
  const gender = normalizeGender(memberGenderRaw)
  const subscriberId = subscriberIdFromCard(fields, memberObj)
  return {
    patientFirstName: firstName,
    patientLastName: lastName,
    patientDOB: dob,
    patientGender: gender,
    subscriberFirstName: firstName,
    subscriberLastName: lastName,
    subscriberDOB: dob,
    relationToSubscriber: 'self',
    memberID: subscriberId,
  }
}

/**
 * Extracts patient/subscriber/insurance fields from a successful Azure analyze payload.
 * @param personId Required when {@link listHealthCardPersons} returns more than one option (`member`, `dep:0`, …).
 */
export function mapAzureHealthInsuranceResultToIntakeForm(
  payload: unknown,
  personId?: string
): IntakeFormPatchFromHealthCard {
  const fields = getFirstDocumentFields(payload)
  if (!fields) {
    console.warn(
      'mapAzureHealthInsuranceResultToIntakeForm: missing analyzeResult.documents[0].fields'
    )
    return { ...EMPTY }
  }

  const memberField = fields.Member
  const memberObj = fieldAsObject(memberField)
  const people = getHealthCardPersonIdentities(fields, memberObj)
  const common = buildCommonInsuranceFields(fields, memberObj)
  const person =
    people.length === 0
      ? personPatchLegacy(fields, memberObj)
      : personPatchFromCardPeople(people, personId ?? people[0].id)

  return {
    ...person,
    ...common,
  }
}
